import React, { useState, useEffect } from "react";
import { getMyOrders, updateOrderItemCustom } from "../services/orderService";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import BillSidebar from "../components/BillSidebar";
import ReturnExchangeSidebar from "../components/ReturnExchangeSidebar";
import CancelItemSidebar from "../components/CancelItemSidebar";
import getOptimizedUrl from "../utils/imageUtils";
import HomeMobileNavbar from "../components/HomeMobileNavbar";
import Navbar from "../components/Navbar";
import { showErrorToast } from "../utils/errorHandler";
import {
  CUSTOM_LENGTH_RANGE,
  CUSTOM_WAIST_RANGE,
  DEFAULT_CUSTOM_LENGTH,
  DEFAULT_CUSTOM_WAIST,
  formatCustomSizeLabel,
  parseCustomMeasurements,
} from "../utils/customSizeUtils";

const CUSTOM_LENGTH_CHART = {
  height: [
    "5'' - 5'2",
    "5'3 - 5'4",
    "5'5 - 5'6",
    "5'7 - 5'8",
    "5'9 - 5'10",
    "5'10 - 6''",
  ],
  recommended: [39, 40, 40, 41, 42, 43],
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billSidebarOpen, setBillSidebarOpen] = useState(false);
  const [billOrder, setBillOrder] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editItemId, setEditItemId] = useState(null);
  const [editCustomLength, setEditCustomLength] = useState(
    DEFAULT_CUSTOM_LENGTH,
  );
  const [editCustomWaist, setEditCustomWaist] = useState(DEFAULT_CUSTOM_WAIST);

  // New states for Return/Exchange and Cancel sidebars
  const [returnExchangeSidebarOpen, setReturnExchangeSidebarOpen] =
    useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [cancelSidebarOpen, setCancelSidebarOpen] = useState(false);
  const [itemToCancel, setItemToCancel] = useState(null);
  const [orderIdForCancel, setOrderIdForCancel] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await getMyOrders();
      setOrders(data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      showErrorToast(error, "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleTrackOrder = async (order) => {
    // If order has AWB, open Delhivery tracking page in new tab
    if (order.delhivery?.waybill) {
      window.open(
        `https://delhivery.com/track-v2/package/${order.delhivery.waybill}`,
        "_blank",
      );
    } else {
      toast.error("Tracking information not available yet");
    }
  };

  const handleTrackExchangeOrder = async (waybill) => {
    const trimmedWaybill = String(waybill || "").trim();

    if (!trimmedWaybill) {
      toast.error("Exchange tracking information not available yet");
      return;
    }

    window.open(
      `https://www.delhivery.com/track-v2/package/${trimmedWaybill}`,
      "_blank",
    );
  };

  const handleViewBill = (order) => {
    const billableItems = (order.items || []).filter(
      (item) =>
        item.cancelledStatus !== "cancelled" &&
        (!item.returnStatus ||
          item.returnStatus === "none" ||
          item.returnStatus === "rejected") &&
        (!item.exchangeStatus ||
          item.exchangeStatus === "none" ||
          item.exchangeStatus === "rejected"),
    );

    setBillOrder({
      ...order,
      items: billableItems,
    });
    setBillSidebarOpen(true);
  };

  const handleReturnExchange = (item, orderId, paymentMethod) => {
    setSelectedItem({ ...item, paymentMethod });
    setSelectedOrderId(orderId);
    setReturnExchangeSidebarOpen(true);
  };

  const handleCancelOrder = (item, orderId) => {
    setItemToCancel(item);
    setOrderIdForCancel(orderId);
    setCancelSidebarOpen(true);
  };

  const handleSidebarSuccess = () => {
    fetchOrders();
    toast.success("Request submitted successfully");
  };

  const canReturnOrExchange = (order) => {
    if (order.orderStage !== "delivered") return false;
    if (!order.deliveredAt) return false;

    const daysSinceDelivery =
      (Date.now() - new Date(order.deliveredAt).getTime()) /
      (1000 * 60 * 60 * 24);
    return daysSinceDelivery <= 5;
  };

  const canCancelInWindow = (order) => {
    if (!order?.createdAt) return false;
    if (!["ordered", "being_made"].includes(order.orderStage)) {
      return false;
    }

    const createdAtMs = new Date(order.createdAt).getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    return Date.now() <= createdAtMs + oneDayMs;
  };

  const getCancellationClosedMessage = (order) => {
    if (!order?.createdAt) {
      return "Cancellation window closed after 24 hours of placing order";
    }

    const closeAt = new Date(
      new Date(order.createdAt).getTime() + 24 * 60 * 60 * 1000,
    );
    return `Cancellation window closed on ${closeAt.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getShipmentEta = (order) =>
    order?.delhivery?.expected_delivery_date ||
    order?.estimatedDelivery ||
    null;

  const getTimelineFillPercent = (currentStageIndex, totalStages) => {
    if (totalStages <= 1) return 100;
    const safeIndex = Math.max(0, Math.min(currentStageIndex, totalStages - 1));
    const rawPercent = (safeIndex / (totalStages - 1)) * 100;
    const minVisiblePercent = 12;
    return Math.max(minVisiblePercent, rawPercent);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div>
      {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
      <div className="min-h-screen bg-white py-8 md:py-12 px-4 sm:px-6 lg:px-8 font-bdogrotesk">
        <BillSidebar
          isOpen={billSidebarOpen}
          onClose={() => setBillSidebarOpen(false)}
          order={billOrder}
        />

        <ReturnExchangeSidebar
          isOpen={returnExchangeSidebarOpen}
          onClose={() => setReturnExchangeSidebarOpen(false)}
          orderItem={selectedItem}
          orderId={selectedOrderId}
          onSuccess={handleSidebarSuccess}
        />

        <CancelItemSidebar
          isOpen={cancelSidebarOpen}
          onClose={() => setCancelSidebarOpen(false)}
          item={itemToCancel}
          orderId={orderIdForCancel}
          onSuccess={handleSidebarSuccess}
        />

        {/* Edit Custom Modal - moved here so it renders regardless of orders length */}
        {editModalOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/60 z-[10000]"
              onClick={() => setEditModalOpen(false)}
            />
            <div
              className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
              onClick={() => setEditModalOpen(false)}
            >
              <div
                className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-5 md:p-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-end mb-4">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="text-base font-medium hover:text-gray-600"
                  >
                    Close
                  </button>
                </div>

                <div className="w-full overflow-x-auto">
                  <table className="w-full min-w-[640px] border-collapse">
                    <tbody>
                      <tr className="border-b border-black/60">
                        <td className="py-3 pr-4 text-base font-medium">
                          Height
                        </td>
                        {CUSTOM_LENGTH_CHART.height.map((h) => (
                          <td
                            key={h}
                            className="py-3 px-2 sm:px-3 text-base font-medium text-left whitespace-nowrap"
                          >
                            {h}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-black/60">
                        <td className="py-3 pr-4 text-base font-medium leading-tight">
                          Recommended
                          <br />
                          Length
                        </td>
                        {CUSTOM_LENGTH_CHART.recommended.map((len, idx) => (
                          <td
                            key={len}
                            className="py-3 px-2 sm:px-3 text-base font-medium text-left whitespace-nowrap"
                          >
                            {idx === CUSTOM_LENGTH_CHART.recommended.length - 1
                              ? `${len}+`
                              : len}
                            "
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="mt-4 text-xs text-gray-600 lg:hidden">
                  Select length and waist
                </p>
                <div className="mt-2 lg:mt-6 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                  <div className="flex items-center gap-3 overflow-x-auto lg:overflow-visible">
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-base font-medium whitespace-nowrap">
                        <span className="lg:hidden">Length</span>
                        <span className="hidden lg:inline">Custom Length</span>
                      </div>
                      <div className="inline-flex items-center border border-black h-12 w-fit">
                        <button
                          type="button"
                          className="w-10 h-full text-2xl leading-none"
                          onClick={() =>
                            setEditCustomLength((prev) =>
                              Math.max(CUSTOM_LENGTH_RANGE.min, prev - 1),
                            )
                          }
                        >
                          -
                        </button>
                        <div className="w-20 text-center text-base font-medium">
                          {editCustomLength}"
                        </div>
                        <button
                          type="button"
                          className="w-10 h-full text-2xl leading-none"
                          onClick={() =>
                            setEditCustomLength((prev) =>
                              Math.min(CUSTOM_LENGTH_RANGE.max, prev + 1),
                            )
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-base font-medium whitespace-nowrap">
                        <span className="lg:hidden">Waist</span>
                        <span className="hidden lg:inline">Custom Waist</span>
                      </div>
                      <div className="inline-flex items-center border border-black h-12 w-fit">
                        <button
                          type="button"
                          className="w-10 h-full text-2xl leading-none"
                          onClick={() =>
                            setEditCustomWaist((prev) =>
                              Math.max(CUSTOM_WAIST_RANGE.min, prev - 1),
                            )
                          }
                        >
                          -
                        </button>
                        <div className="w-20 text-center text-base font-medium">
                          {editCustomWaist}"
                        </div>
                        <button
                          type="button"
                          className="w-10 h-full text-2xl leading-none"
                          onClick={() =>
                            setEditCustomWaist((prev) =>
                              Math.min(CUSTOM_WAIST_RANGE.max, prev + 1),
                            )
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const customValue = formatCustomSizeLabel(
                          editCustomLength,
                          editCustomWaist,
                        );
                        await updateOrderItemCustom(
                          editOrderId,
                          editItemId,
                          customValue,
                        );
                        toast.success("Customization updated");
                        setEditModalOpen(false);
                        fetchOrders();
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update customization");
                      }
                    }}
                    className="px-4 sm:px-6 h-12 bg-black text-white text-base font-medium w-full lg:w-auto lg:ml-auto"
                  >
                    Save Custom Size
                  </button>
                </div>

                <p className="mt-5 text-base font-medium leading-relaxed">
                  Customisations are{" "}
                  <span className="font-semibold">free of cost</span>! |
                  Customised pieces are not applicable for return/exchange as
                  they're made just for you!
                </p>
              </div>
            </div>
          </>
        )}

        <div className="max-w-full mx-auto">
          <div className="mb-8 md:mb-12">
            {/* RESPONSIVE: text-3xl on mobile, text-5xl on desktop */}
            <h1 className="text-3xl md:text-5xl font-normal text-black">
              Order History
            </h1>
          </div>

          {orders.length === 0 ? (
            <div className="p-8 md:p-12 text-center border-t border-black">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Orders Yet
              </h3>
              <button
                onClick={() => navigate("/store")}
                className="inline-flex items-center px-6 py-3 border border-black text-base font-medium text-white bg-black hover:bg-gray-800"
              >
                Browse Store
              </button>
            </div>
          ) : (
            <div className="space-y-12 md:space-y-16">
              {orders.flatMap((order) => {
                // Separate items into those with individual status and normal items
                const itemsWithIndividualStatus = order.items.filter(
                  (item) =>
                    (item.returnStatus && item.returnStatus !== "none") ||
                    (item.exchangeStatus && item.exchangeStatus !== "none") ||
                    (item.cancelledStatus &&
                      item.cancelledStatus === "cancelled"),
                );
                const normalItems = order.items.filter(
                  (item) =>
                    (!item.returnStatus || item.returnStatus === "none") &&
                    (!item.exchangeStatus || item.exchangeStatus === "none") &&
                    (!item.cancelledStatus ||
                      item.cancelledStatus !== "cancelled"),
                );

                const cards = [];

                // Normal items: always show standard delivery timeline
                if (normalItems.length > 0) {
                  const stages = [
                    "ordered",
                    "being_made",
                    "shipped",
                    "delivered",
                  ];
                  const stageLabels = {
                    ordered: "Ordered",
                    being_made: "Being Made",
                    shipped: "Shipped",
                    delivered: "Delivered",
                  };
                  let currentStageIndex = stages.indexOf(order.orderStage);
                  if (currentStageIndex < 0) currentStageIndex = 3;

                  cards.push(
                    <div
                      key={order._id}
                      className="border-t border-black pt-6 md:pt-8"
                    >
                      {/* Header Row */}
                      <div>
                        <div className="mb-6 flex flex-col md:flex-row gap-4 md:gap-32">
                          <div className="flex flex-col">
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Order Number
                            </div>
                            <div className="text-sm font-medium text-black">
                              #{order._id.slice(-5).toUpperCase()}
                            </div>

                            <div className="flex-shrink-0 w-full md:w-40 pt-10">
                              <div className="relative pl-4">
                                <div className="absolute left-0 top-1 bottom-1 w-[6px] bg-gray-300"></div>
                                <div
                                  className="absolute left-0 top-1 w-[6px] bg-black"
                                  style={{
                                    height: `${getTimelineFillPercent(currentStageIndex, stages.length)}%`,
                                    maxHeight: "calc(100% - 0.5rem)",
                                  }}
                                ></div>
                                {stages.map((stage, index) => {
                                  const isPast = index <= currentStageIndex;
                                  const isCurrent = index === currentStageIndex;
                                  return (
                                    <div
                                      key={stage}
                                      className="relative flex items-center mb-5 last:mb-0"
                                    >
                                      {/* <div className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full border-2 ${isPast ? "bg-black border-black" : "bg-white border-gray-300"}`}></div> */}
                                      <span
                                        className={`text-sm ${isCurrent ? "text-black font-semibold" : isPast ? "text-black" : "text-gray-400"}`}
                                      >
                                        {stageLabels[stage]}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              {(order.orderStage === "shipped" ||
                                order.orderStage === "delivered") &&
                                order.delhivery?.waybill && (
                                  <button
                                    type="button"
                                    onClick={() => handleTrackOrder(order)}
                                    className="mt-4 ml-4 text-sm font-medium text-black underline"
                                  >
                                    Track Order
                                  </button>
                                )}
                            </div>
                          </div>
                          <div className="w-full md:flex-1 px-0 md:px-8">
                            <div className="flex flex-wrap gap-2 md:justify-between md:gap-2">
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  Order Date
                                </div>
                                <div className="text-sm font-medium text-black">
                                  {formatDate(order.createdAt)}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  {order.orderStage === "delivered"
                                    ? "Delivery Date"
                                    : order.orderStage === "shipped"
                                      ? "Est. Delivery Date"
                                      : "Delivery Status"}
                                </div>
                                <div className="text-sm font-medium text-black">
                                  {order.orderStage === "delivered"
                                    ? formatDate(
                                        order.deliveredAt || order.updatedAt,
                                      )
                                    : order.orderStage === "shipped"
                                      ? getShipmentEta(order)
                                        ? formatDate(getShipmentEta(order))
                                        : "Updating..."
                                      : "—"}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  Ship To
                                </div>
                                <div className="text-sm font-medium text-black uppercase">
                                  {order.shippingAddress?.city},{" "}
                                  {order.shippingAddress?.state}
                                </div>
                              </div>
                              <div className="items-center gap-4">
                                <button
                                  onClick={() => handleViewBill(order)}
                                  className="text-sm text-black underline"
                                >
                                  View Receipt
                                </button>
                                <div className="text-lg font-semibold text-black">
                                  ₹{order.totalPrice?.toLocaleString("en-IN")}
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 space-y-6 mt-8">
                              {normalItems.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="flex flex-col sm:flex-row"
                                >
                                  <div className="w-full sm:w-72 aspect-square sm:aspect-auto sm:h-72 bg-white flex-shrink-0 overflow-hidden">
                                    {item.image ? (
                                      <img
                                        src={getOptimizedUrl(
                                          item.image || "",
                                          800,
                                        )}
                                        alt={item.productName}
                                        className="w-full h-full object-cover bg-white"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                                        No Image
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 sm:ml-4 mt-3 sm:mt-0 flex flex-col justify-between py-1">
                                    <div>
                                      <h3 className="text-xl md:text-3xl font-semibold font-bdogrotesk text-black">
                                        {item.productName} - {item.size}
                                      </h3>
                                      <p className="text-sm text-black font-bdogrotesk font-medium mt-1">
                                        Qty: {item.quantity}
                                      </p>
                                      {((item.customSize &&
                                        item.customSize.trim()) ||
                                        String(
                                          item.size || "",
                                        ).toLowerCase() === "custom") && (
                                        <div className="mt-2">
                                          <p className="text-sm text-gray-600">
                                            {item.customSize ||
                                              "(Custom selected)"}
                                          </p>
                                          {order.orderStage === "ordered" && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditOrderId(order._id);
                                                setEditItemId(item._id);
                                                const parsed =
                                                  parseCustomMeasurements(
                                                    item.customSize,
                                                  );
                                                setEditCustomLength(
                                                  parsed.length,
                                                );
                                                setEditCustomWaist(
                                                  parsed.waist,
                                                );
                                                setEditModalOpen(true);
                                              }}
                                              className="text-sm text-black underline mt-1"
                                            >
                                              Edit Customization
                                            </button>
                                          )}
                                        </div>
                                      )}
                                      {order.orderStage === "ordered" && (
                                        <p className="text-xs text-gray-500 mt-3">
                                          Cancellation window open until order
                                          is processed (usually within 24 hours)
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap gap-3 mt-3">
                                      {order.orderStage === "delivered" ? (
                                        canReturnOrExchange(order) ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleReturnExchange(
                                                  item,
                                                  order._id,
                                                  order.paymentMethod,
                                                )
                                              }
                                              className="text-sm border border-solid border-black px-4 py-2 text-black"
                                            >
                                              Return/Exchange
                                            </button>
                                          </>
                                        ) : (
                                          <span className="text-xs text-gray-500">
                                            Return/Exchange window closed (5
                                            days after delivery)
                                          </span>
                                        )
                                      ) : order.orderStage === "cancelled" ? (
                                        <span className="text-sm text-red-600 font-medium">
                                          Order Cancelled
                                          {order.cancellationReason
                                            ? ` — ${order.cancellationReason}`
                                            : ""}
                                        </span>
                                      ) : canCancelInWindow(order) ? (
                                        <button
                                          onClick={() =>
                                            handleCancelOrder(item, order._id)
                                          }
                                          className="text-sm border border-solid border-black px-4 py-2 text-black"
                                        >
                                          Cancel Item
                                        </button>
                                      ) : (
                                        <span className="text-xs text-gray-500">
                                          {getCancellationClosedMessage(order)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Body: Timeline Left + Items Right */}
                    </div>,
                  );
                }

                // Split items: each gets its own card with appropriate timeline
                itemsWithIndividualStatus.forEach((item) => {
                  const itemReturnStatus = item.returnStatus || "none";
                  const itemExchangeStatus = item.exchangeStatus || "none";
                  const itemCancelledStatus = item.cancelledStatus || "none";
                  const isItemCancelled = itemCancelledStatus === "cancelled";
                  const isItemExchangeFlow = itemExchangeStatus !== "none";
                  const isItemReturnFlow = itemReturnStatus !== "none";

                  let splitStages, splitLabels, splitCurrentIndex;

                  if (isItemCancelled) {
                    splitStages = ["cancelled"];
                    splitLabels = { cancelled: "Cancelled" };
                    splitCurrentIndex = 0;
                  } else if (isItemExchangeFlow) {
                    if (itemExchangeStatus === "rejected") {
                      splitStages = [
                        "exchange_requested",
                        "processing",
                        "exchange_not_possible",
                      ];
                      splitLabels = {
                        exchange_requested: "Exchange Requested",
                        processing: "Processing",
                        exchange_not_possible: "Exchange Not Possible",
                      };
                      splitCurrentIndex = 2;
                    } else {
                      splitStages = [
                        "exchange_requested",
                        "processing",
                        "parcel_received",
                        "new_order_shipped",
                        "order_delivered",
                      ];
                      splitLabels = {
                        exchange_requested: "Exchange Requested",
                        processing: "Processing",
                        parcel_received: "Parcel Received",
                        new_order_shipped: "New Order Shipped",
                        order_delivered: "Order Delivered",
                      };

                      splitCurrentIndex =
                        itemExchangeStatus === "requested" ? 0 : 1;
                      const exchangeShipmentStatus = String(
                        order.delhivery?.exchangeShipmentStatus || "",
                      ).toLowerCase();
                      const hasExchangeShipment = Boolean(
                        order.delhivery?.exchangeWaybill,
                      );
                      const exchangeShipmentDelivered =
                        Boolean(order.delhivery?.exchangeDeliveredAt) ||
                        exchangeShipmentStatus.includes("delivered") ||
                        exchangeShipmentStatus.includes("completed");

                      const exchangeRequestedAtMs = item.exchangeRequestDate
                        ? new Date(item.exchangeRequestDate).getTime()
                        : 0;
                      const stageOccurredAfterExchangeRequest = (stage) =>
                        (order.stageHistory || []).some(
                          (entry) =>
                            entry?.stage === stage &&
                            new Date(entry?.timestamp || 0).getTime() >=
                              exchangeRequestedAtMs,
                        );

                      if (
                        stageOccurredAfterExchangeRequest(
                          "return_received_to_store",
                        )
                      ) {
                        splitCurrentIndex = Math.max(splitCurrentIndex, 2);
                      }
                      if (
                        hasExchangeShipment ||
                        stageOccurredAfterExchangeRequest("shipped")
                      ) {
                        splitCurrentIndex = Math.max(splitCurrentIndex, 3);
                      }
                      if (
                        exchangeShipmentDelivered ||
                        stageOccurredAfterExchangeRequest("delivered")
                      ) {
                        splitCurrentIndex = Math.max(splitCurrentIndex, 4);
                      }

                      if (itemExchangeStatus === "completed") {
                        splitCurrentIndex = 4;
                      }
                    }
                  } else if (isItemReturnFlow) {
                    splitStages = [
                      "return_requested",
                      "processing",
                      "parcel_received",
                      "refund_initiated",
                    ];
                    splitLabels = {
                      return_requested: "Return Requested",
                      processing: "Processing",
                      parcel_received: "Parcel Received",
                      refund_initiated: "Refund Initiated",
                    };
                    const statusToIndex = {
                      requested: 0,
                      approved: 1,
                      rejected: 0,
                      completed: 3,
                    };
                    const activeStatus =
                      itemReturnStatus !== "none"
                        ? itemReturnStatus
                        : itemExchangeStatus;
                    splitCurrentIndex = statusToIndex[activeStatus] ?? 0;
                    if (order.orderStage === "return_processing")
                      splitCurrentIndex = Math.max(splitCurrentIndex, 1);
                    if (order.orderStage === "return_received_to_store")
                      splitCurrentIndex = Math.max(splitCurrentIndex, 2);
                    if (order.orderStage === "refund_initiated")
                      splitCurrentIndex = Math.max(splitCurrentIndex, 3);
                  } else {
                    splitStages = [
                      "ordered",
                      "being_made",
                      "shipped",
                      "delivered",
                    ];
                    splitLabels = {
                      ordered: "Ordered",
                      being_made: "Being Made",
                      shipped: "Shipped",
                      delivered: "Delivered",
                    };
                    splitCurrentIndex = splitStages.indexOf(order.orderStage);
                    if (splitCurrentIndex < 0) splitCurrentIndex = 3;
                  }

                  cards.push(
                    <div
                      key={`${order._id}-${item._id}`}
                      className="border-t border-black pt-6 md:pt-8"
                    >
                      <div>
                        <div className="mb-6 flex flex-col md:flex-row gap-4 md:gap-32">
                          <div className="flex flex-col">
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                              Order Number
                            </div>
                            <div className="text-sm font-medium text-black">
                              #{order._id.slice(-5).toUpperCase()}
                            </div>

                            <div className="flex-shrink-0 w-full md:w-40 pt-10">
                              <div className="relative pl-4">
                                <div className="absolute left-0 top-1 bottom-1 w-[6px] bg-gray-300"></div>
                                <div
                                  className="absolute left-0 top-1 w-[6px] bg-black"
                                  style={{
                                    height: `${getTimelineFillPercent(splitCurrentIndex, splitStages.length)}%`,
                                    maxHeight: "calc(100% - 0.5rem)",
                                  }}
                                ></div>
                                {splitStages.map((stage, index) => {
                                  const isPast = index <= splitCurrentIndex;
                                  const isCurrent = index === splitCurrentIndex;
                                  return (
                                    <div
                                      key={stage}
                                      className="relative flex items-center mb-5 last:mb-0"
                                    >
                                      <span
                                        className={`text-sm ${isCurrent ? "text-black font-semibold" : isPast ? "text-black" : "text-gray-400"}`}
                                      >
                                        {splitLabels[stage] || stage}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              {isItemExchangeFlow &&
                              order.delhivery?.exchangeWaybill ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleTrackExchangeOrder(
                                      order.delhivery?.exchangeWaybill,
                                    )
                                  }
                                  className="mt-4 ml-4 text-sm font-medium text-black underline"
                                >
                                  Track Exchange Order
                                </button>
                              ) : (
                                (order.orderStage === "shipped" ||
                                  order.orderStage === "delivered") &&
                                order.delhivery?.waybill && (
                                  <button
                                    type="button"
                                    onClick={() => handleTrackOrder(order)}
                                    className="mt-4 ml-4 text-sm font-medium text-black underline"
                                  >
                                    Track Order
                                  </button>
                                )
                              )}
                            </div>
                          </div>

                          <div className="w-full md:flex-1 px-0 md:px-8">
                            <div className="flex flex-wrap gap-2 md:justify-between md:gap-2">
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  Order Date
                                </div>
                                <div className="text-sm font-medium text-black">
                                  {formatDate(order.createdAt)}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  {order.orderStage === "delivered"
                                    ? "Delivery Date"
                                    : order.orderStage === "shipped"
                                      ? "Est. Delivery Date"
                                      : "Delivery Status"}
                                </div>
                                <div className="text-sm font-medium text-black">
                                  {order.orderStage === "delivered"
                                    ? formatDate(
                                        order.deliveredAt || order.updatedAt,
                                      )
                                    : order.orderStage === "shipped"
                                      ? getShipmentEta(order)
                                        ? formatDate(getShipmentEta(order))
                                        : "Updating..."
                                      : "—"}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  Ship To
                                </div>
                                <div className="text-sm font-medium text-black uppercase">
                                  {order.shippingAddress?.city},{" "}
                                  {order.shippingAddress?.state}
                                </div>
                              </div>
                              <div className="items-center gap-4">
                                <button
                                  onClick={() => handleViewBill(order)}
                                  className="text-sm text-black underline"
                                >
                                  View Receipt
                                </button>
                                <div className="text-lg font-semibold text-black">
                                  ₹{order.totalPrice?.toLocaleString("en-IN")}
                                </div>
                              </div>
                            </div>

                            <div className="mt-8">
                              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                                <div className="w-full sm:w-72 aspect-square sm:aspect-auto sm:h-72 bg-white flex-shrink-0 overflow-hidden">
                                  {item.image ? (
                                    <img
                                      src={getOptimizedUrl(
                                        item.image || "",
                                        800,
                                      )}
                                      alt={item.productName}
                                      className="w-full h-full object-cover bg-white"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                                      No Image
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 flex flex-col justify-between py-1">
                                  <div>
                                    <h3 className="text-lg md:text-xl font-semibold text-black">
                                      {item.productName}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Size: {item.size} · Qty: {item.quantity}
                                    </p>
                                    <span
                                      className={`inline-block mt-2 px-3 py-1 text-xs font-medium rounded-full 
                                      }`}
                                    >
                                      {isItemCancelled
                                        ? "Cancelled"
                                        : itemReturnStatus !== "none"
                                          ? `Return ${itemReturnStatus}`
                                          : itemExchangeStatus === "rejected"
                                            ? "Exchange Not Possible"
                                            : `Exchange ${itemExchangeStatus}`}
                                    </span>
                                  </div>
                                  {/* <p className="text-sm text-gray-500 mt-3">
                                    {isItemCancelled
                                      ? "This item has been cancelled"
                                      : `${itemReturnStatus !== "none" ? "Return" : "Exchange"} request submitted`}
                                  </p> */}
                                  {itemExchangeStatus === "rejected" && (
                                    <div className="mt-3">
                                      <span className="text-sm border border-solid border-black px-4 py-2 text-black inline-block">
                                        Exchange is not possible for this item
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>,
                  );
                });

                return cards;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;
